import { useCartMinimums } from '../../utils/useCartMinimums'
import React, { useEffect, useState } from 'react'
import { Button } from '@mui/material'
import CartItem from './CartItem/CartItem'
import { IProduct } from '../../types/products'
import { useAppNavigate } from '../../context/AppContext'
import { Trans, useTranslation } from 'react-i18next'
import { DateTime } from 'luxon'
import './Cart.scss'
import { getStorageKey } from '../../utils/appType'

type Props = {
  cart: IProduct[]
  setCart: React.Dispatch<React.SetStateAction<IProduct[]>>
}
const c = 'cart-card'

const Cart = (props: Props) => {
  const navigate = useAppNavigate()
  const { checkedCart } = useCartMinimums(props.cart)
  const { t } = useTranslation()
  const [isButtonDisabled, setIsButtonDisabled] = useState(false)
  const generalInfo = JSON.parse(
    localStorage.getItem(getStorageKey('info')) || '{}',
  )

  useEffect(() => {
    const targetTimezone = 'Europe/Chisinau'
    const targetTimeBefore = DateTime.now()
      .setZone(targetTimezone)
      .set({
        hour: Number(generalInfo?.minTime?.split(':')[0] || 10),
        minute: Number(generalInfo?.minTime?.split(':')[1] || 0),
        second: 0,
      })
    const targetTimeAfter = DateTime.now()
      .setZone(targetTimezone)
      .set({
        hour: Number(generalInfo?.maxTime?.split(':')[0] || 23),
        minute: Number(generalInfo?.maxTime?.split(':')[1] || 0),
        second: 0,
      })

    const currentTime = DateTime.now().setZone(targetTimezone)

    const isBefore10AM = currentTime < targetTimeBefore
    const isAfter11PM = currentTime > targetTimeAfter

    setIsButtonDisabled(isBefore10AM || isAfter11PM)
  }, [])

  const totalPrice = props.cart.reduce(
    (acc: number, item: IProduct) =>
      Number(acc) + Number(item?.quantity) * Number(item.price),
    0,
  )

  function disableCart() {
    if (generalInfo.dayOff) {
      return true
    }

    if (isButtonDisabled) {
      return true
    }

    if (!props.cart.length) {
      return true
    }

    if (Number(totalPrice) < Number(generalInfo?.minPrice)) {
      return true
    }

    return false
  }

  return (
    <div className={c}>
      <div className={`${c}__data`}>
        {props.cart?.length ? (
          checkedCart.map((item: IProduct) => (
            <CartItem key={item._id} data={item} setCart={props.setCart} />
          ))
        ) : (
          <div className={`${c}-no_data`}>{t('NO_PRODUCT_SELECTED')}</div>
        )}
      </div>
      <div className={`${c}-divider`} />
      <div className={`${c}-total`}>
        <div className={`${c}-total-label`}>{t('TOTAL')}:</div>
        <div className={`${c}-total-value`}>{totalPrice} lei</div>
      </div>
      {Number(totalPrice) < Number(generalInfo?.minPrice) && (
        <div className={`${c}-note`}>
          <Trans
            i18nKey='MINIMUM_PRICE'
            values={{ minPrice: generalInfo?.minPrice }}
          />
        </div>
      )}
      <div className={`${c}-add_to_cart`}>
        <Button
          variant='contained'
          disabled={disableCart()}
          onClick={() => navigate('/cart')}
          sx={{
            backgroundColor: 'var(--color-primary)',
            textTransform: 'none',
            fontWeight: 'bold',
            width: '100%',
            '&:hover': { backgroundColor: 'var(--color-primary-hover)' },
          }}
        >
          {t('CART')}
        </Button>
      </div>
      {isButtonDisabled && !generalInfo.dayOff && (
        <div className={`${c}-note-time_limit`}>
          <Trans
            i18nKey='TIME_LIMIT'
            values={{
              minTime: generalInfo?.minTime,
              maxTime: generalInfo.maxTime,
            }}
          />
        </div>
      )}
      {generalInfo?.dayOff && (
        <div className={`${c}-note-time_limit`}>
          <Trans i18nKey='DAY_OFF' />
        </div>
      )}
    </div>
  )
}

export default Cart
